/*
 * Team aggregation for the tournament comparison view.
 *
 * A team has two players. To let the SAME court + comparison components render
 * a team exactly like a single player, we fold a team's players into one
 * synthetic `PlayerStatistics`:
 *
 *  - running_distance  → sum (both players' court coverage combined)
 *  - mean_velocity     → mean of means (a fair team average)
 *  - max_velocity      → max (the team's fastest burst)
 *  - court_zone        → averaged fractions (each player's distribution sums to
 *                        1, so the average does too — still a valid share)
 *  - heatmap           → element-wise sum of the grids (the team's combined
 *                        court occupancy, rendered in one hue)
 *  - shots_tracking    → outcomes/distribution merged; performance rates
 *                        recounted over the merged shot list, so a busier
 *                        player naturally weighs the team rate more
 *
 * The result is consumed in-memory only (never re-validated), so it just has
 * to be structurally faithful to PlayerStatistics for the components.
 */

import type { PlayerStatistics } from '@/types/statistics';
import { shotRates } from './shotTypes';

type ShotsTracking = PlayerStatistics['shots_tracking'];
type ShotOutcome = NonNullable<ShotsTracking>['shot_outcomes'][string][number];

/** Element-wise sum of equally-sized grids (mismatched ones are skipped). */
function sumGrids(grids: number[][][]): number[][] {
  const valid = grids.filter((g) => Array.isArray(g) && g.length > 0);
  if (valid.length === 0) return [];
  const rows = valid[0].length;
  const cols = valid[0][0]?.length ?? 0;
  const out = Array.from({ length: rows }, () =>
    new Array<number>(cols).fill(0),
  );
  for (const g of valid) {
    if (g.length !== rows || (g[0]?.length ?? 0) !== cols) continue;
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) out[i][j] += g[i][j] ?? 0;
    }
  }
  return out;
}

function mergeShots(list: ShotsTracking[]): ShotsTracking {
  const present = list.filter((s): s is NonNullable<ShotsTracking> => !!s);
  if (present.length === 0) return null;

  // Accepted shots only — keep team totals/counts consistent with the per-player
  // views, which all drop low-confidence detections (see selectShots).
  const shot_outcomes: Record<string, ShotOutcome[]> = {};
  for (const st of present) {
    for (const [type, arr] of Object.entries(st.shot_outcomes)) {
      (shot_outcomes[type] ??= []).push(...arr.filter((s) => s.accepted));
    }
  }

  const shot_distribution: NonNullable<ShotsTracking>['shot_distribution'] = {};
  for (const st of present) {
    for (const [type, dist] of Object.entries(st.shot_distribution)) {
      const target = (shot_distribution[type] ??= {
        horizontal_positions: {},
        vertical_positions: {},
      });
      for (const [k, v] of Object.entries(dist.horizontal_positions)) {
        target.horizontal_positions[k] =
          (target.horizontal_positions[k] ?? 0) + v;
      }
      for (const [k, v] of Object.entries(dist.vertical_positions)) {
        target.vertical_positions[k] = (target.vertical_positions[k] ?? 0) + v;
      }
    }
  }

  // Rates recounted over the merged shot list rather than averaged from the
  // members' published ones — exact instead of count-weighted, and derived the
  // same way as a single player's, so a team row and its two player rows can't
  // tell different stories.
  const performance: NonNullable<ShotsTracking>['performance'] = {};
  const toPerf = (shots: ShotOutcome[]) => {
    const r = shotRates(shots);
    return r
      ? {
          in_rate: r.inRate,
          error_rate: r.errorRate,
          winner_rate: r.winnerRate,
        }
      : { in_rate: null, error_rate: null, winner_rate: null };
  };
  for (const [type, shots] of Object.entries(shot_outcomes)) {
    performance[type] = toPerf(shots);
  }
  performance.global = toPerf(Object.values(shot_outcomes).flat());

  return { shot_outcomes, shot_distribution, performance };
}

/** Fold a team's players into one synthetic, component-ready PlayerStatistics. */
export function aggregateTeamStatistics(
  players: PlayerStatistics[],
): PlayerStatistics {
  const first = players[0];
  const n = players.length || 1;
  const sum = (pick: (p: PlayerStatistics) => number) =>
    players.reduce((a, p) => a + pick(p), 0);
  const avgZone = (pick: (z: PlayerStatistics['court_zone']) => number) =>
    players.reduce((a, p) => a + pick(p.court_zone), 0) / n;

  const zones: Record<string, number> = {};
  for (const key of Object.keys(first.court_zone.zones)) {
    zones[key] =
      players.reduce((a, p) => a + (p.court_zone.zones[key] ?? 0), 0) / n;
  }

  return {
    running_distance: sum((p) => p.running_distance),
    mean_velocity: sum((p) => p.mean_velocity) / n,
    max_velocity: Math.max(...players.map((p) => p.max_velocity)),
    court_zone: {
      front_back: {
        net: avgZone((z) => z.front_back.net),
        transition: avgZone((z) => z.front_back.transition),
        back: avgZone((z) => z.front_back.back),
      },
      sides: {
        left: avgZone((z) => z.sides.left),
        middle: avgZone((z) => z.sides.middle),
        right: avgZone((z) => z.sides.right),
      },
      zones,
    },
    heatmap: {
      ...first.heatmap,
      heatmap: sumGrids(players.map((p) => p.heatmap.heatmap)),
      total_points: sum((p) => p.heatmap.total_points),
    },
    shots_tracking: mergeShots(players.map((p) => p.shots_tracking)),
    coaching_tips: null,
  };
}
